import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

/**
 * Accordion — dark luxury single-open-at-a-time accordion.
 * Props:
 *   items: [{ q: string, a: string | ReactNode }]
 *   testIdPrefix: string (default "accordion")
 */
export const Accordion = ({ items = [], testIdPrefix = "accordion" }) => {
    const [openIdx, setOpenIdx] = useState(null);

    return (
        <div className="w-full" data-testid={testIdPrefix}>
            {items.map((it, i) => {
                const isOpen = openIdx === i;
                return (
                    <div
                        key={i}
                        className="border-b border-border-subtle"
                        data-testid={`${testIdPrefix}-item-${i}`}
                    >
                        <button
                            type="button"
                            onClick={() => setOpenIdx(isOpen ? null : i)}
                            aria-expanded={isOpen}
                            data-testid={`${testIdPrefix}-toggle-${i}`}
                            className="group flex w-full items-center justify-between gap-6 py-6 text-left transition-colors duration-200 hover:text-[var(--accent)]"
                        >
                            <span className="font-display text-[18px] font-medium leading-snug text-text-primary sm:text-[20px]">
                                {it.q}
                            </span>
                            <span
                                aria-hidden
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ease-luxury ${
                                    isOpen
                                        ? "border-[rgba(124,255,79,0.5)] bg-[rgba(124,255,79,0.06)] text-[var(--accent)]"
                                        : "border-border-subtle text-text-muted group-hover:border-border-default group-hover:text-text-primary"
                                }`}
                            >
                                {isOpen ? (
                                    <Minus
                                        className="h-4 w-4"
                                        strokeWidth={1.75}
                                    />
                                ) : (
                                    <Plus
                                        className="h-4 w-4"
                                        strokeWidth={1.75}
                                    />
                                )}
                            </span>
                        </button>
                        <AnimatePresence initial={false}>
                            {isOpen && (
                                <motion.div
                                    key="answer"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{
                                        duration: 0.35,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="overflow-hidden"
                                >
                                    <div
                                        className="pb-7 pr-14 text-[15.5px] leading-[1.7] text-text-secondary"
                                        data-testid={`${testIdPrefix}-answer-${i}`}
                                    >
                                        {it.a}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            })}
        </div>
    );
};

export default Accordion;
